import React from "react";
import PropTypes from "prop-types";

import { useBreakpoint } from "../WidgetBreakpoint";
import { useTranslation } from "react-i18next";

import { XAxis, YAxis, FlexibleXYPlot, VerticalRectSeries } from "react-vis";
import "react-vis/dist/style.css";

import { Colours } from "@learningblocks/shared-components";

export default function BarChart4(props) {
  const {
    t,
    // i18n
  } = useTranslation("translation");

  const breakpoints = useBreakpoint();

  const data = props.widgetData.Histogram4.slice(0, props.sampleSize);

  function std(data) {
    const mean = data.reduce((a, b) => a + b) / data.length;
    const diff = data.map((n) => n - mean);
    const sqs = diff.map((n) => n * n);
    const sum = sqs.reduce((s, current) => s + current, 0);

    return Math.sqrt(sum / data.length);
  }

  function sortNumber(a, b) {
    return a - b;
  }

  function quantile(array, percentile) {
    array.sort(sortNumber);
    const index = (percentile / 100) * (array.length - 1);
    let result;
    if (Math.floor(index) == index) {
      result = array[index];
    } else {
      const i = Math.floor(index);
      const fraction = index - i;
      result = array[i] + (array[i + 1] - array[i]) * fraction;
    }
    return result;
  }

  const barXYData = (requirement) => {
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min;

    const n = data.length;
    let bins;
    let binWidth;

    if (props.hist === 1) {
      // Algo 1 (Sturges)
      bins = 1 + Math.log2(n);
      binWidth = range / bins;
    }

    if (props.hist === 2) {
      // Algo 2 (Scott)
      const s = std(data);
      binWidth = (3.5 * s) / n ** (1 / 3);
      bins = range / binWidth;
    }

    if (props.hist === 3) {
      // Algo 3 (FD)
      const v25 = quantile(data, 25);
      const v75 = quantile(data, 75);
      binWidth = (2 * (v75 - v25)) / n ** (1 / 3);
      bins = range / binWidth;
    }

    binWidth = binWidth.toFixed(2);

    const Ys = [];
    const cols = [];
    for (let i = 1; i < bins + 1; i++) {
      const col = {
        x: min + binWidth * i - 0.2,
        y: 0,
        x0: min + binWidth * (i - 1) + 0.2,
        y0: 0,
      };
      data.map((val) => {
        if (min + binWidth * (i - 1) <= val && val <= min + binWidth * i) {
          col.y++;
        }
      });
      Ys.push(col.y);
      if (col.x > 0) {
        cols.push(col);
      }
    }

    if (requirement === "aria-label") {
      const modesXs = [];
      const maxFrequency = Math.max(...Ys);
      let modeQuantity = 0;
      cols.map((col) => {
        if (col.y === maxFrequency) {
          modeQuantity++;
          modesXs.push({
            xStart: col.x0,
            xEnd: col.x,
          });
        }
      });
      let string = "";
      modesXs.map((mode, index) => {
        if (index === 0) {
          string =
            string +
            t(`{{start_min}} meters to {{start_max}} meters`, {
              start_min: Math.round(mode.xStart * 10) / 10,
              start_max: Math.round(mode.xEnd * 10) / 10,
            });
        } else {
          string =
            string +
            " " +
            t(`and from {{end_min}} meters to {{end_max}} meters`, {
              end_min: Math.round(mode.xStart * 10) / 10,
              end_max: Math.round(mode.xEnd * 10) / 10,
            });
        }
      });

      const str = bins.toString();
      const newStr = str.split(".");
      const ariaLabel = `${t(`Summary description: This histogram has ${newStr[0]} ${newStr[0] > 1 ? "bins" : "bin"}.`)} ${t(`There ${modeQuantity > 1 ? "are" : "is"} {{modeQuantity}} ${modeQuantity > 1 ? "modes" : "mode"}.`, { modeQuantity: modeQuantity })} ${t(`With a frequency of {{maxFrequency}} from {{string}}`, { maxFrequency: maxFrequency, string: string })}`;
      return ariaLabel;
    }

    if (requirement === "table") {
      return (
        <table style={{ width: props.width }}>
          <tbody>
            <tr>
              <th
                style={{
                  padding: "8px",
                  textAlign: "left",
                  color: "white",
                  fontWeight: "bold",
                }}
              >
                {t("Bins")}
              </th>
              <th
                style={{
                  padding: "8px",
                  textAlign: "left",
                  color: "white",
                  fontWeight: "bold",
                }}
              >
                {t("Counts")}
              </th>
            </tr>
            {cols.map((col, index) => {
              return (
                <tr key={index}>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      color: "white",
                      backgroundColor: index % 2 === 0 ? "rgb(0,62,107)" : null,
                    }}
                  >
                    {t(`{{start_m}} meters to {{end_m}} meters`, {
                      start_m: Math.round(col.x0 * 10) / 10,
                      end_m: Math.round(col.x * 10) / 10,
                    })}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      color: "white",
                      backgroundColor: index % 2 === 0 ? "rgb(0,62,107)" : null,
                      textAlign: "center",
                    }}
                  >
                    {col.y}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }

    return cols;
  };

  return (
    <div
      className="bar-chart"
      style={{
        background: "#002F52",
        border: "1px solid " + Colours.navy4,
        boxSizing: "border-box",
        borderRadius: "5px",
        padding: "20px",
        paddingBottom: breakpoints.xs || breakpoints.sm ? "15px" : "20px",
        marginTop: breakpoints.xs || breakpoints.sm ? "-5px" : null,
        paddingTop: "60px",
        position: "relative",
      }}
    >
      <div
        aria-label={barXYData("aria-label")}
        style={{ height: "0px", width: "0px", overflow: "hidden" }}
      >
        {barXYData("aria-label")}
      </div>
      <button
        style={{
          cursor: "pointer",
          border: "none",
          minWidth: "130px",
          height: "38px",
          borderRadius: "5px",
          backgroundColor: "white",
          color: "black",
          position: "absolute",
          top: "15px",
          right: "15px",
          fontSize: "14px",
        }}
        onClick={() => {
          if (props.display === "table") {
            props.setDisplay("histogram");
          } else {
            props.setDisplay("table");
          }
        }}
        aria-label={t(
          `Toggle the representation of the data to a ${props.display === "table" ? "histogram" : "table"}`
        )}
      >
        {t(`View as ${props.display === "table" ? "histogram" : "table"}`)}
      </button>
      {props.display === "table" ? <div>{barXYData("table")}</div> : null}
      {props.display === "histogram" ? (
        <>
          <FlexibleXYPlot
            xDomain={[0, 100]}
            yDomain={[0, 250]}
            width={props.width}
            height={props.height}
          >
            <VerticalRectSeries
              animation
              color={Colours.yellow1}
              data={barXYData()}
            />
            <XAxis
              tickValues={[0, 20, 40, 60, 80, 100]}
              tickFormat={(v) => Math.trunc(v)}
              style={{ text: { fill: "white" } }}
            />
            <YAxis
              tickFormat={(v) => Math.trunc(v)}
              style={{ text: { fill: "white" } }}
            />
            <p
              style={{
                position: "absolute",
                left: "-35px",
                bottom: "calc(50% - 10px)",
                color: "white",
                fontSize: "14px",
                transform: "rotate(-90deg)",
                textAnchor: "middle",
                width: "fit-content",
              }}
            >
              {t("Frequency")}
            </p>
            <p
              style={{
                position: "absolute",
                right: "calc(50% - 30px)",
                bottom: "-20px",
                color: "white",
                fontSize: "14px",
                textAnchor: "middle",
                width: "fit-content",
              }}
            >
              {t("Value")}
            </p>
          </FlexibleXYPlot>
        </>
      ) : null}
    </div>
  );
}

BarChart4.propTypes = {
  width: PropTypes.int,
  height: PropTypes.int,
  type: PropTypes.string,
  visible: PropTypes.boolean,
  hist: PropTypes.string,
};
